import {
  getEndOfDayWithTimezone,
  getEndOfMonthWithTimezone,
  getEndOfWeekWithTimezone,
  getEndOfYearWithTimezone,
  getStartOfDayWithTimezone,
  getStartOfMonthWithTimezone,
  getStartOfWeekWithTimezone,
  getStartOfYearWithTimezone,
} from "@redotech/util/date";
import { assertNever, safeAssertNever, Tuple } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  AdvancedFilterType,
  createAdvancedFilterDataSchema,
  GenericFilterBuilder,
} from "./generic-advanced-filter-data";

export enum DateFilterOperator {
  WITHIN = "within",
  BEFORE = "before",
  AFTER = "after",
}
const DateFilterOperatorSchema = createEnum(DateFilterOperator, {
  enumName: "DateFilterOperator",
});

export enum KnownDateFilterTimeFrame {
  TODAY = "today",
  THIS_WEEK = "this week",
  LAST_WEEK = "last week",
  THIS_MONTH = "this month",
  LAST_MONTH = "last month",
  THIS_YEAR = "this year",
  LAST_YEAR = "last year",
  CUSTOM = "custom",
  RELATIVE = "relative",
}
const KnownDateFilterTimeFrameSchema = createEnum(KnownDateFilterTimeFrame, {
  enumName: "KnownDateFilterTimeFrame",
});

// Coerce string/number to Date for serialization through RPC/Temporal
const dateCoercion = z.preprocess((val) => {
  if (val instanceof Date) {
    return val;
  }
  if (typeof val === "string" || typeof val === "number") {
    return new Date(val);
  }
  return val;
}, z.date());

export const TwoDateTupleSchema = z.tuple([dateCoercion, dateCoercion]);
export const OneDateTupleSchema = z.tuple([dateCoercion]);
export type TwoDateTuple = Tuple<Date, 2>;
export type OneDateTuple = Tuple<Date, 1>;

const CustomDateSchema = z.union([OneDateTupleSchema, TwoDateTupleSchema]);
export type CustomDate = z.infer<typeof CustomDateSchema>;

export enum RelativeTimeUnit {
  MINUTES = "minutes",
  HOURS = "hours",
  DAYS = "days",
}
const RelativeTimeUnitSchema = createEnum(RelativeTimeUnit, {
  enumName: "RelativeTimeUnit",
});

export const SingleRelativeTimeSchema = z.object({
  value: z.number().positive(),
  unit: RelativeTimeUnitSchema,
});
export type SingleRelativeTime = z.infer<typeof SingleRelativeTimeSchema>;

export const RelativeTimeRangeSchema = z.object({
  start: SingleRelativeTimeSchema,
  end: SingleRelativeTimeSchema,
});
export type RelativeTimeRange = z.infer<typeof RelativeTimeRangeSchema>;

export const RelativeTimeSchema = z.union([
  SingleRelativeTimeSchema,
  RelativeTimeRangeSchema,
]);
export type RelativeTime = z.infer<typeof RelativeTimeSchema>;

export function isRelativeTimeRange(
  relativeTime: RelativeTime,
): relativeTime is RelativeTimeRange {
  return "start" in relativeTime && "end" in relativeTime;
}

export const DateFilterDataSchema = createAdvancedFilterDataSchema(
  AdvancedFilterType.DATE,
  KnownDateFilterTimeFrameSchema,
  DateFilterOperatorSchema,
).extend({
  customDate: CustomDateSchema.nullish(),
  relativeTime: RelativeTimeSchema.nullish(),
});
export type DateFilterData = z.infer<typeof DateFilterDataSchema>;

export const DateFilterBuilder: GenericFilterBuilder<
  KnownDateFilterTimeFrame,
  DateFilterOperator,
  DateFilterData
> = {
  type: AdvancedFilterType.DATE,
  valueSchema: KnownDateFilterTimeFrameSchema,
  operatorSchema: DateFilterOperatorSchema,
  schema: DateFilterDataSchema,
  addOpensearchFilters: ({ filter, opensearchPath, compound, timeZoneId }) => {
    const { operator, value, customDate, relativeTime } = filter;
    if (!value) {
      return;
    }

    switch (operator) {
      case DateFilterOperator.WITHIN: {
        const dates = getWithinDatesForDateFilter(
          value,
          customDate,
          timeZoneId,
          relativeTime,
        );
        if (dates) {
          compound.filter.push({
            range: {
              [opensearchPath]: {
                gte: dates.start.toISOString(),
                lte: dates.end.toISOString(),
              },
            },
          });
        }

        break;
      }
      case DateFilterOperator.BEFORE: {
        const date = getBeforeDateForDateFilter(
          value,
          customDate,
          timeZoneId,
          relativeTime,
        );
        if (date) {
          compound.filter.push({
            range: { [opensearchPath]: { lt: date.toISOString() } },
          });
        }

        break;
      }
      case DateFilterOperator.AFTER: {
        const date = getAfterDateForDateFilter(
          value,
          customDate,
          timeZoneId,
          relativeTime,
        );
        if (date) {
          compound.filter.push({
            range: { [opensearchPath]: { gt: date.toISOString() } },
          });
        }

        break;
      }
      default:
        safeAssertNever(operator);
    }
  },
  readFromString(name, operatorValueEncoded, defaultFilter) {
    try {
      const decoded = decodeURIComponent(operatorValueEncoded);
      const operatorValueDate = decoded.split("::");

      const operator = operatorValueDate?.[0];
      const value = operatorValueDate?.[1];
      const customDateString = operatorValueDate?.[2];

      if (!operator) {
        return defaultFilter;
      }

      const parsedOperator = DateFilterOperatorSchema.safeParse(operator);
      if (!parsedOperator.success) {
        console.warn("Invalid operator type");
        return undefined;
      }

      if (!value) {
        return {
          type: AdvancedFilterType.DATE,
          name,
          value: null,
          operator: parsedOperator.data,
          customDate: undefined,
        };
      }

      const timeframeParsed = KnownDateFilterTimeFrameSchema.safeParse(
        value.trim(),
      );

      if (!timeframeParsed.success) {
        console.warn("Invalid value type for DateFilter");
        return undefined;
      }

      const parsedValue = timeframeParsed.data;

      let parsedCustomDate = undefined;
      let parsedRelativeTime = undefined;

      if (parsedValue === KnownDateFilterTimeFrame.CUSTOM) {
        try {
          parsedCustomDate = CustomDateSchema.parse(
            ((JSON.parse(customDateString ?? "") as string[]) || []).map(
              (d) => new Date(d),
            ),
          );
        } catch (error) {
          return {
            type: AdvancedFilterType.DATE,
            name,
            value: parsedValue,
            operator: parsedOperator.data,
            customDate: undefined,
            relativeTime: undefined,
          };
        }
      } else if (parsedValue === KnownDateFilterTimeFrame.RELATIVE) {
        try {
          const relativeTimeString = operatorValueDate?.[2];
          if (relativeTimeString) {
            parsedRelativeTime = RelativeTimeSchema.parse(
              JSON.parse(relativeTimeString),
            );
          }
        } catch (error) {
          return {
            type: AdvancedFilterType.DATE,
            name,
            value: parsedValue,
            operator: parsedOperator.data,
            customDate: undefined,
            relativeTime: undefined,
          };
        }
      }

      return {
        type: AdvancedFilterType.DATE,
        name,
        value: parsedValue,
        operator: parsedOperator.data,
        customDate:
          parsedValue === KnownDateFilterTimeFrame.CUSTOM
            ? parsedCustomDate
            : undefined,
        relativeTime:
          parsedValue === KnownDateFilterTimeFrame.RELATIVE
            ? parsedRelativeTime
            : undefined,
      };
    } catch (error) {
      console.error("Error parsing DateFilter", error);
      return undefined;
    }
  },
  writeToString(filter) {
    try {
      if (!filter) {
        return "";
      }
      const { operator, value, customDate, relativeTime } = filter;
      const parsedValue = KnownDateFilterTimeFrameSchema.safeParse(value);

      if (parsedValue.success) {
        let stringValue = `${operator}::${parsedValue.data}`;

        if (
          parsedValue.data === KnownDateFilterTimeFrame.CUSTOM &&
          customDate
        ) {
          stringValue = `${operator}::${parsedValue.data}::${JSON.stringify(customDate)}`;
        } else if (
          parsedValue.data === KnownDateFilterTimeFrame.RELATIVE &&
          relativeTime
        ) {
          stringValue = `${operator}::${parsedValue.data}::${JSON.stringify(relativeTime)}`;
        }

        return stringValue;
      } else {
        return `${operator}::`;
      }
    } catch (error) {
      console.error("Error writing DateFilter", error);
      return "";
    }
  },
  isPartial(filter) {
    return (
      !filter.value ||
      !filter.operator ||
      (filter.value === KnownDateFilterTimeFrame.CUSTOM &&
        filter.customDate == undefined) ||
      (filter.value === KnownDateFilterTimeFrame.RELATIVE &&
        filter.relativeTime == undefined)
    );
  },
};

function calculateRelativeDate(
  relativeTime: SingleRelativeTime,
  timeZoneId?: string | null,
): Date {
  const tz = timeZoneId || Temporal.Now.timeZoneId();
  const now = Temporal.Now.zonedDateTimeISO(tz);

  let duration: Temporal.Duration;
  switch (relativeTime.unit) {
    case RelativeTimeUnit.MINUTES:
      duration = Temporal.Duration.from({ minutes: relativeTime.value });
      break;
    case RelativeTimeUnit.HOURS:
      duration = Temporal.Duration.from({ hours: relativeTime.value });
      break;
    case RelativeTimeUnit.DAYS:
      duration = Temporal.Duration.from({ days: relativeTime.value });
      break;
    default:
      assertNever(relativeTime.unit);
  }

  const pastTime = now.subtract(duration);
  return new Date(pastTime.toInstant().epochMilliseconds);
}

export function getWithinDatesForDateFilter(
  timeFrame: KnownDateFilterTimeFrame,
  customDate: CustomDate | undefined | null,
  timeZoneId?: string | null,
  relativeTime?: RelativeTime | undefined | null,
): { start: Date; end: Date } {
  const tz = timeZoneId || Temporal.Now.timeZoneId();

  if (timeFrame === KnownDateFilterTimeFrame.CUSTOM) {
    if (!customDate) {
      throw new Error(
        "Custom date was specified, but no custom date was provided",
      );
    }
    if (customDate.length !== 2) {
      throw new Error(
        "Custom date was specified, but provided custom date is a date range",
      );
    }
    return { start: customDate[0], end: customDate[1] };
  }

  if (timeFrame === KnownDateFilterTimeFrame.RELATIVE) {
    if (!relativeTime) {
      throw new Error(
        "Relative time was specified, but no relative time was provided",
      );
    }

    if (isRelativeTimeRange(relativeTime)) {
      const startDate = calculateRelativeDate(relativeTime.end, tz);
      const endDate = calculateRelativeDate(relativeTime.start, tz);
      return { start: startDate, end: endDate };
    } else {
      // Single relative time means "from X ago to now"
      const startDate = calculateRelativeDate(relativeTime, tz);
      const endDate = new Date();
      return { start: startDate, end: endDate };
    }
  }

  const now = Temporal.Now.zonedDateTimeISO(tz).toPlainDate();

  switch (timeFrame) {
    case KnownDateFilterTimeFrame.TODAY: {
      return {
        start: getStartOfDayWithTimezone(now, tz),
        end: getEndOfDayWithTimezone(now, tz),
      };
    }
    case KnownDateFilterTimeFrame.THIS_WEEK: {
      return {
        start: getStartOfWeekWithTimezone(now, tz),
        end: getEndOfWeekWithTimezone(now, tz),
      };
    }
    case KnownDateFilterTimeFrame.LAST_WEEK: {
      const oneWeekAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ weeks: 1 })
        .toPlainDate();

      return {
        start: getStartOfWeekWithTimezone(oneWeekAgo, tz),
        end: getEndOfWeekWithTimezone(oneWeekAgo, tz),
      };
    }
    case KnownDateFilterTimeFrame.THIS_MONTH: {
      return {
        start: getStartOfMonthWithTimezone(now, tz),
        end: getEndOfDayWithTimezone(now, tz),
      };
    }
    case KnownDateFilterTimeFrame.LAST_MONTH: {
      const oneMonthAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ months: 1 })
        .toPlainDate();

      return {
        start: getStartOfMonthWithTimezone(oneMonthAgo, tz),
        end: getEndOfMonthWithTimezone(oneMonthAgo, tz),
      };
    }
    case KnownDateFilterTimeFrame.THIS_YEAR: {
      return {
        start: getStartOfYearWithTimezone(now, tz),
        end: getEndOfYearWithTimezone(now, tz),
      };
    }
    case KnownDateFilterTimeFrame.LAST_YEAR: {
      const oneYearAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ years: 1 })
        .toPlainDate();

      return {
        start: getStartOfYearWithTimezone(oneYearAgo, tz),
        end: getEndOfYearWithTimezone(oneYearAgo, tz),
      };
    }
    default:
      return assertNever(timeFrame);
  }
}

export function getAfterDateForDateFilter(
  timeFrame: KnownDateFilterTimeFrame,
  customDate: CustomDate | undefined | null,
  timeZoneId?: string | null,
  relativeTime?: RelativeTime | undefined | null,
): Date {
  const tz = timeZoneId || Temporal.Now.timeZoneId();

  if (timeFrame === KnownDateFilterTimeFrame.CUSTOM) {
    if (!customDate) {
      throw new Error(
        "Custom date was specified, but no custom date was provided",
      );
    }
    if (customDate.length === 2) {
      throw new Error(
        "Single custom date was specified, but provided custom date in date range",
      );
    }
    return customDate[0];
  }

  if (timeFrame === KnownDateFilterTimeFrame.RELATIVE) {
    if (!relativeTime) {
      throw new Error(
        "Relative time was specified, but no relative time was provided",
      );
    }

    if (isRelativeTimeRange(relativeTime)) {
      return calculateRelativeDate(relativeTime.start, tz);
    } else {
      return calculateRelativeDate(relativeTime, tz);
    }
  }

  const now = Temporal.Now.zonedDateTimeISO(tz).toPlainDate();
  switch (timeFrame) {
    case KnownDateFilterTimeFrame.TODAY: {
      return getEndOfDayWithTimezone(now, tz);
    }
    case KnownDateFilterTimeFrame.THIS_WEEK: {
      return getEndOfWeekWithTimezone(now, tz);
    }
    case KnownDateFilterTimeFrame.LAST_WEEK: {
      const oneWeekAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ weeks: 1 })
        .toPlainDate();
      return getEndOfWeekWithTimezone(oneWeekAgo, tz);
    }
    case KnownDateFilterTimeFrame.THIS_MONTH: {
      return getEndOfMonthWithTimezone(now, tz);
    }
    case KnownDateFilterTimeFrame.LAST_MONTH: {
      const oneMonthAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ months: 1 })
        .toPlainDate();
      return getEndOfMonthWithTimezone(oneMonthAgo, tz);
    }
    case KnownDateFilterTimeFrame.THIS_YEAR: {
      return getEndOfYearWithTimezone(now, tz);
    }
    case KnownDateFilterTimeFrame.LAST_YEAR: {
      const oneYearAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ years: 1 })
        .toPlainDate();
      return getEndOfYearWithTimezone(oneYearAgo, tz);
    }
    default:
      return assertNever(timeFrame);
  }
}

export function getBeforeDateForDateFilter(
  timeFrame: KnownDateFilterTimeFrame,
  customDate: CustomDate | undefined | null,
  timeZoneId?: string | null,
  relativeTime?: RelativeTime | undefined | null,
): Date {
  const tz = timeZoneId || Temporal.Now.timeZoneId();

  if (timeFrame === KnownDateFilterTimeFrame.CUSTOM) {
    if (!customDate) {
      throw new Error(
        "Custom date was specified, but no custom date was provided",
      );
    }
    if (customDate.length === 2) {
      throw new Error(
        "Single custom date was specified, but provided custom date in date range",
      );
    }
    return customDate[0];
  }

  if (timeFrame === KnownDateFilterTimeFrame.RELATIVE) {
    if (!relativeTime) {
      throw new Error(
        "Relative time was specified, but no relative time was provided",
      );
    }

    if (isRelativeTimeRange(relativeTime)) {
      return calculateRelativeDate(relativeTime.end, tz);
    } else {
      return calculateRelativeDate(relativeTime, tz);
    }
  }

  const now = Temporal.Now.zonedDateTimeISO(tz).toPlainDate();

  switch (timeFrame) {
    case KnownDateFilterTimeFrame.TODAY: {
      return getStartOfDayWithTimezone(now, tz);
    }
    case KnownDateFilterTimeFrame.THIS_WEEK: {
      return getStartOfWeekWithTimezone(now, tz);
    }
    case KnownDateFilterTimeFrame.LAST_WEEK: {
      const oneWeekAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ weeks: 1 })
        .toPlainDate();
      return getStartOfWeekWithTimezone(oneWeekAgo, tz);
    }
    case KnownDateFilterTimeFrame.THIS_MONTH: {
      return getStartOfMonthWithTimezone(now, tz);
    }
    case KnownDateFilterTimeFrame.LAST_MONTH: {
      const oneMonthAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ months: 1 })
        .toPlainDate();
      return getStartOfMonthWithTimezone(oneMonthAgo, tz);
    }
    case KnownDateFilterTimeFrame.THIS_YEAR: {
      return getStartOfYearWithTimezone(now, tz);
    }
    case KnownDateFilterTimeFrame.LAST_YEAR: {
      const oneYearAgo = Temporal.Now.zonedDateTimeISO(tz)
        .subtract({ years: 1 })
        .toPlainDate();
      return getStartOfYearWithTimezone(oneYearAgo, tz);
    }
    default:
      return assertNever(timeFrame);
  }
}
